/**
 * Profanity filter for player-submitted names.
 *
 * Uses the `bad-words` library (offline word list) to quickly block common
 * English profanity and basic leetspeak. Mirrors the backend check so the
 * user gets instant feedback before hitting the API.
 */
import { Filter } from 'bad-words';

const filter = new Filter();

// Common leetspeak character substitutions. Applied as a pre-normalization
// pass before checking, so "sh!t" / "f*ck" / "a$$" get caught.
const LEET_MAP = {
  '!': 'i', '1': 'i', '|': 'i',
  '3': 'e',
  '4': 'a', '@': 'a',
  '0': 'o',
  '5': 's', '$': 's',
  '7': 't', '+': 't',
  '8': 'b',
  '*': '',
};

function normalizeLeet(input) {
  let out = '';
  for (const ch of input.toLowerCase()) {
    out += LEET_MAP[ch] ?? ch;
  }
  return out;
}

/**
 * Returns true if the given name contains profanity.
 * Safe to call on empty/undefined input.
 */
export function isProfane(name) {
  if (!name || typeof name !== 'string') return false;
  try {
    if (filter.isProfane(name)) return true;
    // Second pass: normalize leetspeak and re-check
    const normalized = normalizeLeet(name);
    return normalized !== name.toLowerCase() && filter.isProfane(normalized);
  } catch {
    return false;
  }
}

/**
 * Character mask for player name inputs.
 *
 * Strips every character except Unicode letters, spaces, hyphens, and
 * apostrophes (straight ' and curly ’). This prevents players from using
 * digits or symbols to bypass the profanity filter (e.g. "sh1t", "a$$").
 *
 * Meant to be applied in real-time inside an input's onChange handler so
 * disallowed characters never appear in the field — acting as a mask.
 */
const ALLOWED_NAME_CHAR_RE = /[\p{L} '\u2019-]/u;

export const MAX_NAME_LENGTH = 20;

export function sanitizeName(input) {
  if (!input) return '';
  let out = '';
  for (const ch of String(input)) {
    if (ALLOWED_NAME_CHAR_RE.test(ch)) out += ch;
    if (out.length >= MAX_NAME_LENGTH) break;
  }
  return out;
}
